import React, { useState, useMemo } from 'react'
import { Drawer, Checkbox } from '@material-ui/core'
import { Close as CloseIcon, Search as SearchIcon } from '@material-ui/icons'
import axios from 'axios'
import { tokens } from '../../../styles/desertFloraTheme'

/* ── Constants ─────────────────────────────────────────────────── */

const CATEGORY_ORDER = [
  'General Conditions', 'General', 'Demo & Clearing', 'Demo', 'Earthwork & Grading', 'Earthwork',
  'Drainage & Erosion Control', 'Irrigation', 'Hardscape', 'Surfacing',
  'Planting', 'Lighting', 'Chemical Applications', 'Water Feature', 'Closeout & Warranty'
]

const categoryRank = (category) => {
  const idx = CATEGORY_ORDER.indexOf(category)
  return idx === -1 ? CATEGORY_ORDER.length + 1 : idx
}

const serviceSearchText = (service) => [
  service?.name,
  service?.serviceName,
  service?.serviceId,
  service?.category,
  service?.serviceCategory,
  ...(service?.aliases || [])
].filter(Boolean).join(' ').toLowerCase()

const TABS = ['From catalog', 'From template', 'Quick add (LS)']

/* ── Styles ────────────────────────────────────────────────────── */

const drawerPaper = {
  width: 520,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  background: tokens.surface,
  fontFamily: tokens.fontUi
}

const headerBar = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '16px 20px 0',
}

const titleStyle = {
  fontFamily: "'Instrument Serif', Georgia, serif",
  fontSize: 18, fontWeight: 400,
  color: tokens.ink, margin: 0
}

const closeBtn = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: tokens.ink3, padding: 6, borderRadius: 8,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  transition: 'background .12s'
}

const searchWrap = {
  position: 'relative', padding: '12px 20px 0'
}

const searchInputStyle = {
  width: '100%', boxSizing: 'border-box',
  fontFamily: tokens.fontUi,
  fontSize: 13,
  background: tokens.surface,
  border: `1px solid ${tokens.line}`,
  borderRadius: 8,
  padding: '8px 12px 8px 32px',
  outline: 'none',
  color: tokens.ink,
  transition: 'border-color .12s, box-shadow .12s'
}

const tabBar = {
  display: 'flex', gap: 0,
  borderBottom: `1px solid ${tokens.line}`,
  padding: '0 20px',
  marginTop: 12
}

const sectionHeader = {
  fontFamily: tokens.fontMono,
  fontSize: 10.5, fontWeight: 600,
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  color: tokens.ink3,
  padding: '12px 0 6px',
  borderBottom: `1px solid ${tokens.line}`,
  margin: '0 20px'
}

const roleTag = (color, bg) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  fontFamily: tokens.fontMono,
  fontSize: 10, fontWeight: 600,
  letterSpacing: '.04em',
  textTransform: 'uppercase',
  color,
  background: bg,
  border: '1px solid currentColor',
  borderRadius: 999,
  padding: '1px 7px 1px 5px',
  whiteSpace: 'nowrap', flexShrink: 0
})

const emptyState = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flex: 1, color: tokens.ink4,
  fontFamily: tokens.fontMono,
  fontSize: 13, fontWeight: 500
}

/* ── Component ─────────────────────────────────────────────────── */

const ServicePicker = ({ open, onClose, services, projectServices, projectId, url, onServiceAdded, onServiceRemoved }) => {
  const [search, setSearch] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  const [activeTab, setActiveTab] = useState(0)

  const activeServiceIds = useMemo(() => {
    return new Set((projectServices || []).map(ps => ps.serviceId))
  }, [projectServices])

  const psLookup = useMemo(() => {
    const map = {}
    for (const ps of (projectServices || [])) map[ps.serviceId] = ps
    return map
  }, [projectServices])

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return (services || [])
      .filter(s => !q || serviceSearchText(s).includes(q))
      .sort((a, b) => {
        const catDiff = categoryRank(a.category) - categoryRank(b.category)
        if (catDiff !== 0) return catDiff
        return String(a.name || '').localeCompare(String(b.name || ''))
      })
  }, [services, search])

  const { assemblies, baseServices } = useMemo(() => {
    const asmList = []
    const baseList = []
    for (const s of filtered) {
      if (s.serviceRole === 'assembly') {
        asmList.push(s)
      } else {
        baseList.push(s)
      }
    }
    return { assemblies: asmList, baseServices: baseList }
  }, [filtered])

  const handleToggle = async (svc) => {
    if (activeServiceIds.has(svc.serviceId)) {
      const ps = psLookup[svc.serviceId]
      if (ps) {
        try {
          await axios.delete(`${url}/api/bid-estimation/project-services/${ps._id}`)
          onServiceRemoved()
        } catch (e) {
          console.error('Remove failed:', e)
        }
      }
    } else {
      try {
        await axios.post(`${url}/api/bid-estimation/projects/${projectId}/project-services`, {
          serviceId: svc.serviceId,
          serviceName: svc.name,
          serviceCategory: svc.category
        })
        onServiceAdded()
      } catch (e) {
        console.error('Add failed:', e)
        if (e.response?.status === 409) alert('Service already added to this project')
      }
    }
  }

  const renderRow = (svc) => {
    const isActive = activeServiceIds.has(svc.serviceId)
    const isAssembly = svc.serviceRole === 'assembly'

    return (
      <label
        key={svc.serviceId}
        style={{
          display: 'flex', alignItems: 'center',
          padding: '8px 20px', gap: 10, cursor: 'pointer',
          opacity: isActive ? 0.65 : 1,
          transition: 'background .1s'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = tokens.bg2 }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
      >
        <Checkbox
          checked={isActive}
          onChange={() => handleToggle(svc)}
          size="small"
          style={{ color: isActive ? tokens.primary : tokens.ink4, padding: 4 }}
        />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{
            display: 'block', fontSize: 13, fontWeight: isActive ? 600 : 400,
            color: tokens.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
          }}>
            {svc.name}
          </span>
          <span style={{
            display: 'block', fontSize: 11, color: tokens.ink4,
            fontFamily: tokens.fontMono
          }}>
            {svc.category || 'Other'} · {svc.serviceId}
          </span>
        </span>
        {isAssembly ? (
          <span style={roleTag('#6e3fb7', '#ece2fa')}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor', flexShrink: 0 }} />
            Assembly
          </span>
        ) : (
          <span style={roleTag('#3454c1', '#e6ebfa')}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor', flexShrink: 0 }} />
            Service
          </span>
        )}
      </label>
    )
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      BackdropProps={{ style: { backdropFilter: 'blur(4px)' } }}
    >
      <div style={drawerPaper}>
        {/* ── Header ── */}
        <div style={headerBar}>
          <h2 style={titleStyle}>Add to estimate</h2>
          <button
            style={closeBtn}
            onClick={onClose}
            onMouseEnter={(e) => { e.currentTarget.style.background = tokens.bg2 }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'none' }}
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        {/* ── Search ── */}
        <div style={searchWrap}>
          <SearchIcon style={{
            position: 'absolute', left: 30, top: '50%', transform: 'translateY(-50%)',
            fontSize: 16, color: tokens.ink4, pointerEvents: 'none'
          }} />
          <input
            type="text"
            placeholder="Search services..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            style={{
              ...searchInputStyle,
              borderColor: searchFocused ? tokens.primary : tokens.line,
              boxShadow: searchFocused ? '0 0 0 3px rgba(31,94,58,.18)' : 'none'
            }}
          />
        </div>

        {/* ── Tabs ── */}
        <div style={tabBar}>
          {TABS.map((label, i) => (
            <button
              key={label}
              onClick={() => setActiveTab(i)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontFamily: tokens.fontUi,
                fontSize: 13, fontWeight: 600,
                color: activeTab === i ? tokens.ink : tokens.ink3,
                padding: '10px 16px',
                borderBottom: activeTab === i ? `2px solid ${tokens.primary}` : '2px solid transparent',
                transition: 'color .12s, border-color .12s',
                marginBottom: -1
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── Tab content ── */}
        {activeTab === 0 ? (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {/* Assemblies section */}
            {assemblies.length > 0 && (
              <>
                <div style={sectionHeader}>Assemblies · {assemblies.length}</div>
                {assemblies.map(renderRow)}
              </>
            )}

            {/* Base services section */}
            {baseServices.length > 0 && (
              <>
                <div style={sectionHeader}>Base services · {baseServices.length}</div>
                {baseServices.map(renderRow)}
              </>
            )}

            {assemblies.length === 0 && baseServices.length === 0 && (
              <div style={emptyState}>
                No services match your search
              </div>
            )}
          </div>
        ) : (
          <div style={emptyState}>
            Coming soon
          </div>
        )}
      </div>
    </Drawer>
  )
}

export default ServicePicker
